import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAdmin } from '../hooks/useAdmin';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Users, UserPlus, Activity, AlertCircle, ShieldCheck, Edit, Trash2, BookOpen } from 'lucide-react';
import WordManagement from '../components/admin/WordManagement';

const StatCard = ({ title, value, icon, color, gradient }: { title: string, value: number | string, icon: React.ReactNode, color: string, gradient: string }) => (
    <motion.div
        whileHover={{ scale: 1.05, y: -5 }}
        className={`rounded-3xl shadow-xl p-6 text-white flex flex-col items-center justify-center text-center ${gradient}`}>
        <div className={`text-5xl mb-3 p-4 rounded-full bg-white/20 ${color}`}>{icon}</div>
        <p className="text-5xl font-bold drop-shadow-md">{value}</p>
        <p className="text-lg font-medium mt-1 opacity-90">{title}</p>
    </motion.div>
);

const AdminPage: React.FC = () => {
    const { users, stats, isLoading, error } = useAdmin();
    const [activeTab, setActiveTab] = useState<'users' | 'words'>('users');

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-blue-100">
                <div className="text-center">
                    <LoadingSpinner size="lg" className="mb-4" />
                    <p className="text-lg text-blue-700 font-semibold">Loading Admin Powers...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-red-100 flex items-center justify-center p-4">
                <div className="text-center max-w-md mx-auto p-8 bg-white rounded-2xl shadow-xl border-4 border-red-300">
                    <AlertCircle className="w-20 h-20 mx-auto text-red-500 mb-4" />
                    <h2 className="text-3xl font-bold text-red-700 mb-2">Access Denied</h2>
                    <p className="text-gray-600 text-lg">Only admins can view this page. {error.message}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-100 to-purple-200 font-sans p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <header className="mb-16 text-center">
                    <motion.h1
                        initial={{ y: -50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 100 }}
                        className="text-7xl font-extrabold text-purple-800 tracking-tight drop-shadow-lg">Admin Dashboard</motion.h1>
                    <motion.p
                        initial={{ y: -30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, type: 'spring' }}
                        className="text-gray-600 mt-4 text-2xl">Welcome, Super User! ✨</motion.p>
                </header>

                {/* Tab Navigation */}
                <div className="flex justify-center mb-8">
                    <div className="bg-white/60 backdrop-blur-lg rounded-2xl p-2 shadow-lg">
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setActiveTab('users')}
                                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 ${
                                    activeTab === 'users'
                                        ? 'bg-purple-500 text-white shadow-lg'
                                        : 'text-gray-600 hover:text-purple-600 hover:bg-white/50'
                                }`}
                            >
                                <Users className="w-5 h-5" />
                                User Management
                            </button>
                            <button
                                onClick={() => setActiveTab('words')}
                                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 ${
                                    activeTab === 'words'
                                        ? 'bg-purple-500 text-white shadow-lg'
                                        : 'text-gray-600 hover:text-purple-600 hover:bg-white/50'
                                }`}
                            >
                                <BookOpen className="w-5 h-5" />
                                Word Management
                            </button>
                        </div>
                    </div>
                </div>

                {activeTab === 'users' ? (
                    <>
                        {/* User Management Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                            <StatCard title="Total Users" value={stats.totalUsers} icon={<Users />} color="text-blue-800" gradient="bg-gradient-to-br from-blue-400 to-blue-600" />
                            <StatCard title="New Users Today" value={stats.newUsersToday} icon={<UserPlus />} color="text-green-800" gradient="bg-gradient-to-br from-green-400 to-green-600" />
                            <StatCard title="Active Users (7d)" value={stats.activeUsers} icon={<Activity />} color="text-purple-800" gradient="bg-gradient-to-br from-purple-400 to-purple-600" />
                        </div>

                        {/* User Management Table */}
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden">
                            <div className="p-8 border-b border-gray-200">
                                <h2 className="text-4xl font-bold text-gray-800">User Management</h2>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-lg">
                                    <thead className="bg-gray-100/50">
                                        <tr>
                                            <th className="px-8 py-5 text-left font-semibold text-gray-600 uppercase tracking-wider">User</th>
                                            <th className="px-8 py-5 text-left font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                                            <th className="px-8 py-5 text-left font-semibold text-gray-600 uppercase tracking-wider">Joined</th>
                                            <th className="px-8 py-5 text-left font-semibold text-gray-600 uppercase tracking-wider">Last Seen</th>
                                            <th className="px-8 py-5 text-center font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users?.map((user, index) => (
                                            <motion.tr
                                                key={user.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                className="hover:bg-purple-50/50 transition-colors duration-200 border-b border-gray-200">
                                                <td className="px-8 py-6">
                                                    <p className="text-gray-900 font-bold">{user.username}</p>
                                                    <p className="text-gray-500 text-sm font-mono">{user.id}</p>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <span className={`px-4 py-2 rounded-full text-base font-semibold inline-flex items-center gap-2 ${user.is_admin ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                                                        {user.is_admin && <ShieldCheck className="w-5 h-5" />} {user.is_admin ? 'Admin' : 'User'}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-6 text-gray-800">{new Date(user.created_at).toLocaleDateString()}</td>
                                                <td className="px-8 py-6 text-gray-800">
                                                    {(user as any).last_sign_in_at ? new Date((user as any).last_sign_in_at).toLocaleString() : 'Never'}
                                                </td>
                                                <td className="px-8 py-6 text-center">
                                                    <div className="flex justify-center items-center gap-4">
                                                        <button className="text-blue-600 hover:text-blue-800 transition-colors p-2 rounded-full hover:bg-blue-100"><Edit className="w-6 h-6" /></button>
                                                        <button className="text-red-600 hover:text-red-800 transition-colors p-2 rounded-full hover:bg-red-100"><Trash2 className="w-6 h-6" /></button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </motion.div>
                    </>
                ) : (
                    /* Word Management */
                    <WordManagement />
                )}
            </div>
        </div>
    );
};

export default AdminPage;